import { useNavigate } from 'react-router-dom';
import {
  Package,
  Smartphone,
  Shirt,
  ChefHat,
  BookOpen,
  Leaf,
  Dumbbell,
  Sparkles,
  ShoppingBasket,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ShopCategory } from './productAdapter';

/**
 * 6-tile category quick-pick block — Fix 2 from Master Prompt v2.0.
 *
 * Renders the 6 most-popular categories (by product count) as
 * tappable tiles with an icon + label + product count. Tiles drive
 * directly into `/shop?c=<category>`. Layout:
 *
 *   - mobile  (≤ 640)  : 2 × 3
 *   - tablet  (≤ 1024) : 3 × 2
 *   - desktop ( > 1024): 6 × 1
 *
 * Icons are looked up by case-insensitive substring match against the
 * category label so the panel admin can rename categories without
 * breaking the icon assignment. Anything unrecognised falls back to
 * a generic `ShoppingBasket`.
 */

/** Substring → icon lookup. Order matters; first match wins. */
const ICON_MAP: ReadonlyArray<[RegExp, LucideIcon]> = [
  [/spice|herbal|wellness|ayur/i, Leaf],
  [/electron|gadget|phone|laptop|audio|camera/i, Smartphone],
  [/apparel|fashion|cloth|shirt|wear/i, Shirt],
  [/home|kitchen|chef|cookware|appliance/i, ChefHat],
  [/book|stationer/i, BookOpen],
  [/sport|fitness|gym|yoga|outdoor/i, Dumbbell],
  [/beauty|cosmetic|fragrance/i, Sparkles],
];

function iconFor(label: string): LucideIcon {
  for (const [re, Icon] of ICON_MAP) {
    if (re.test(label)) return Icon;
  }
  return Package;
}

export interface TopCategoriesTilesProps {
  /** Same `ShopCategory[]` used by the sidebar — first entry is "All". */
  categories: ShopCategory[];
  /** How many tiles to render. Defaults to 6 per v2.0 wireframe. */
  limit?: number;
  className?: string;
}

export function TopCategoriesTiles({
  categories,
  limit = 6,
  className,
}: TopCategoriesTilesProps) {
  const navigate = useNavigate();

  // Drop the sentinel "All" row, sort by product count descending,
  // and take the top N. If the tenant has fewer than `limit`
  // categories, render whatever's available.
  const tiles = categories
    .filter((c) => c.id !== 'all')
    .slice()
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .slice(0, limit);

  if (tiles.length === 0) return null;

  return (
    <div className={className}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-content-primary sm:text-lg">
          Top categories
        </h2>
        <button
          type="button"
          onClick={() => navigate('/shop')}
          className="text-xs font-medium text-primary hover:underline"
        >
          View all →
        </button>
      </div>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map((t) => {
          const Icon = iconFor(t.label);
          return (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/shop?c=${encodeURIComponent(t.id)}`)}
                className="group flex w-full flex-col items-center gap-2 rounded-lg border border-border bg-surface-card p-3 text-center transition-colors hover:border-primary hover:bg-primary-light"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary-light text-primary transition-transform group-hover:scale-105">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="line-clamp-1 text-xs font-semibold text-content-primary sm:text-sm">
                  {t.label}
                </span>
                <span className="text-[10px] text-content-muted">
                  {t.count ?? 0} {(t.count ?? 0) === 1 ? 'item' : 'items'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default TopCategoriesTiles;
